import { useState, useEffect, useCallback } from 'react';

// Use unified date utils
import { moment } from '../../../utils/dateUtils';

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useConfirm } from 'material-ui-confirm';
import { toast } from 'react-toastify';

// Icons
import RefreshIcon from '@mui/icons-material/Refresh';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import BlockIcon from '@mui/icons-material/Block';
import WarningIcon from '@mui/icons-material/Warning';

// Services
import { authLogServices } from '../../../services/authLogServices';

// Styles
import { table_container_base } from '../../../static/styles';

export const BlockedIPsTable = () => {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeExpired, setIncludeExpired] = useState(false);
  const confirm = useConfirm();

  const loadBlocks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authLogServices.getBlockedIps(includeExpired);
      // Handle both array and object response formats
      const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setBlocks(data);
    } catch (error) {
      console.error('Failed to load blocked IPs:', error);
      toast.error('Failed to load blocked IPs');
      setBlocks([]);
    } finally {
      setLoading(false);
    }
  }, [includeExpired]);

  useEffect(() => {
    loadBlocks();
  }, [loadBlocks]);

  const handleUnblock = async (block) => {
    try {
      await confirm({
        title: 'Unblock IP Address',
        description: `Are you sure you want to unblock ${block.ipAddress}?`,
        confirmationText: 'Unblock',
        confirmationButtonProps: { color: 'success' },
      });

      await authLogServices.unblockIp(block.id);
      toast.success(`IP ${block.ipAddress} unblocked successfully`);
      loadBlocks();
    } catch (error) {
      if (error !== undefined) {
        console.error('Failed to unblock IP:', error);
        toast.error('Failed to unblock IP');
      }
    }
  };

  const handlePermanentBlock = async (block) => {
    try {
      await confirm({
        title: 'Permanently Block IP',
        description: (
          <Box>
            <Typography>
              Are you sure you want to permanently block <strong>{block.ipAddress}</strong>?
            </Typography>
            <Typography color="error" sx={{ mt: 1 }}>
              This will prevent all login attempts from this IP address until manually unblocked.
            </Typography>
          </Box>
        ),
        confirmationText: 'Block Permanently',
        confirmationButtonProps: { color: 'error' },
      });

      await authLogServices.permanentBlockIp(block.id);
      toast.success(`IP ${block.ipAddress} permanently blocked`);
      loadBlocks();
    } catch (error) {
      if (error !== undefined) {
        console.error('Failed to permanently block IP:', error);
        toast.error('Failed to permanently block IP');
      }
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return moment(date).tz('America/Los_Angeles').format('MM/DD/YYYY hh:mm:ss A');
  };

  const getRemainingTime = (blockedUntil) => {
    if (!blockedUntil) return null;
    const now = moment();
    const until = moment(blockedUntil);
    if (until.isBefore(now)) return 'Expired';
    
    const duration = moment.duration(until.diff(now));
    const minutes = Math.ceil(duration.asMinutes());
    return `${minutes} min`;
  };

  const blocksList = Array.isArray(blocks) ? blocks : [];
  const activeBlocks = blocksList.filter(b => b.isActive);
  const expiredBlocks = blocksList.filter(b => !b.isActive);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Blocked IP Addresses
          {activeBlocks.length > 0 && (
            <Chip 
              label={activeBlocks.length} 
              color="error" 
              size="small" 
              sx={{ ml: 1 }}
            />
          )}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={includeExpired}
                onChange={(e) => setIncludeExpired(e.target.checked)}
                size="small"
              />
            }
            label="Show Expired"
          />
          <Tooltip title="Refresh">
            <IconButton onClick={loadBlocks} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={table_container_base}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>IP Address</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Failed Attempts</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Last Email</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>First Failed</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Last Failed</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Remaining</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={30} />
                </TableCell>
              </TableRow>
            ) : blocksList.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No blocked IPs</Typography>
                </TableCell>
              </TableRow>
            ) : (
              blocksList.map((block) => (
                <TableRow 
                  key={block.id} 
                  hover
                  sx={{ 
                    opacity: block.isActive ? 1 : 0.6,
                    backgroundColor: block.permanentBlock ? 'error.lighter' : 'inherit'
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {block.ipAddress}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={block.failedAttempts} 
                      color={block.failedAttempts >= 5 ? 'error' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{block.lastAttemptEmail || '-'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDate(block.firstFailedAt)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDate(block.lastFailedAt)}
                  </TableCell>
                  <TableCell>
                    {block.permanentBlock ? (
                      <Chip 
                        icon={<BlockIcon />}
                        label="Permanent" 
                        color="error" 
                        size="small"
                      />
                    ) : block.isActive ? (
                      <Chip 
                        icon={<WarningIcon />}
                        label="Blocked" 
                        color="warning" 
                        size="small"
                      />
                    ) : (
                      <Chip label="Expired" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {block.permanentBlock ? (
                      <Typography variant="body2" color="error">∞</Typography>
                    ) : (
                      <Typography variant="body2" color={block.isActive ? 'warning.main' : 'text.secondary'}>
                        {getRemainingTime(block.blockedUntil)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      {block.isActive && (
                        <Tooltip title="Unblock">
                          <IconButton 
                            size="small" 
                            color="success"
                            onClick={() => handleUnblock(block)}
                          >
                            <LockOpenIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!block.permanentBlock && (
                        <Tooltip title="Block Permanently">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handlePermanentBlock(block)}
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

